<script lang="ts">
	import { format } from 'svelte-i18n';
	import { fade } from 'svelte/transition';
	import { onMount, onDestroy } from 'svelte';
	import { capFirstChar, convert } from '../../../../helpers/utilities';
	import {
		addDate,
		dateFormat,
		startsWeekOnSun,
		dateLabels,
		getMonthsOptions,
		timeFormatUS,
	} from '../../../../helpers/date_helper';
	import {
		Toolbar,
		Breadcrumbs,
		Text,
		ControlsContainer,
		Stack,
		ListItem,
		CardGrid,
		Scroll,
		Loader,
		Datepicker,
		Select,
		IconButton,
	} from '@Shelly/shelly-design-system';
	import LayerChartRenderer from '../../../Chart/LayerChart/LayerChartRenderer.svelte';
	import type {
		ApplianceChartData,
		ApplianceListData,
		PhaseChartData,
		PredictionsRes,
	} from '../utils/types';
	import type { ChartDataInterface } from 'src/types/Chart';
	import type { ColorTypes } from '@Shelly/shelly-design-system/ListItem/ListItem.types';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system/Select/Select.types';
	import {
		applianceColorPalette,
		applianceSystemColorNames,
		chartHeight,
		SNAPSHOT_CACHE_KEY,
		units,
	} from '../utils/constants';
	import { getDynamicPieChartConfig } from 'src/helpers/statistics/config/dynamic_statistics';
	import { DEFAULT_CHART_HEIGHT } from 'src/helpers/statistics/config/chart_config';
	import { AiBreakdownService } from '../utils/aiBreakdownService';

	type PredictionsMode = 'previous' | 'snapshot';

	// Date picker state
	const today = new Date();
	const yesterday = addDate(today, 'd', -1, true);
	const maxDate = yesterday;
	const firstDayOfWeek = startsWeekOnSun() ? 0 : 1;
	const weekdays: any = dateLabels('days');
	const defaultMonthsOptions = getMonthsOptions();
	const dpFormatD = timeFormatUS() ? 'm.d.Y' : 'd.m.Y';

	let selectedDate: Date = yesterday;
	let predictionsData: PredictionsRes | null = null;
	let loading = true;
	let error = false;
	let applianceColorMap: Record<string, string> = {};
	let applianceColorSystemMap: Record<string, ColorTypes> = {};
	let availableDatesSet: Set<string> = new Set();
	let datesLoaded = false;
	let predictionsMode: PredictionsMode = 'snapshot';

	let lastSnapshotTime: number | null = null;
	let minutesSinceSnapshot: number = 0;
	let snapshotInterval: ReturnType<typeof setInterval>;

	function updateSnapshotTime() {
		if (lastSnapshotTime === null) return;
		const diffMs = Date.now() - lastSnapshotTime;
		minutesSinceSnapshot = Math.floor(diffMs / 60000);
	}

	onDestroy(() => {
		if (snapshotInterval) {
			clearInterval(snapshotInterval);
		}
	});

	const predictionsModeOptions: SelectOptionPropsType[] = [
		{
			title: 'Snapshot',
			value: 'snapshot',
		},
		{
			title: 'By date',
			value: 'previous',
		},
	];

	const listItemColorNames = applianceSystemColorNames as ColorTypes[];
	$: selectedModeOption =
		predictionsModeOptions.find((option) => option.value === predictionsMode) ||
		predictionsModeOptions[0];

	onMount(async () => {
		try {
			const cacheStr = localStorage.getItem(SNAPSHOT_CACHE_KEY);
			if (cacheStr) {
				const cached = JSON.parse(cacheStr);
				lastSnapshotTime = cached.timestamp;
				updateSnapshotTime();
			}
		} catch (e) {}

		await fetchAvailableDates();
		fetchPredictions(selectedDate);
		snapshotInterval = setInterval(updateSnapshotTime, 1000);
	});

	function formatDateParam(date: Date): string {
		return dateFormat(date, 'Y-m-d');
	}

	async function fetchPredictions(date?: Date): Promise<void> {
		loading = true;
		error = false;
		predictionsData = null;

		const response =
			predictionsMode === 'snapshot'
				? await AiBreakdownService.getSnapshot()
				: await AiBreakdownService.getPredictions(formatDateParam(date));

		if (response.isok && response.data) {
			if (predictionsMode === 'snapshot') {
				const cacheStr = localStorage.getItem(SNAPSHOT_CACHE_KEY);
				if (cacheStr) {
					try {
						const cached = JSON.parse(cacheStr);
						lastSnapshotTime = cached.timestamp;
					} catch (e) {
						lastSnapshotTime = Date.now();
					}
				} else {
					lastSnapshotTime = Date.now();
				}
				updateSnapshotTime();
			}

			predictionsData = response.data;
			// Create color mapping for appliances
			applianceColorMap = {};
			applianceColorSystemMap = {};
			predictionsData.byAppliance.forEach((item, index) => {
				applianceColorMap[item.appliance] =
					applianceColorPalette[index % applianceColorPalette.length];
				applianceColorSystemMap[item.appliance] =
					listItemColorNames[index % listItemColorNames.length];
			});

			loading = false;
		} else {
			error = true;
			loading = false;
		}
	}

	function onDateChange(e: CustomEvent): void {
		if (predictionsMode === 'snapshot') return;
		const date = e.detail[0]?.[0] || e.detail[1];
		if (date && date.getTime() !== selectedDate.getTime()) {
			selectedDate = new Date(date);
			fetchPredictions(selectedDate);
		}
	}

	function onModeChange(e: CustomEvent): void {
		const modeValue = e.detail?.value;
		if (modeValue === predictionsMode) return;

		predictionsMode = modeValue;
		fetchPredictions(selectedDate);
	}

	async function fetchAvailableDates(): Promise<void> {
		const response = await AiBreakdownService.getDatesWithPredictions();
		if (response.isok && response.data && response.data.length > 0) {
			availableDatesSet = new Set(response.data);

			const latestDateStr = availableDatesSet.values().next().value;
			const [year, month, day] = latestDateStr.split('-').map(Number);
			selectedDate = new Date(year, month - 1, day);
		}
		datesLoaded = true;
	}

	function isDateDisabled(date: Date): boolean {
		if (!datesLoaded || availableDatesSet.size === 0) return false;
		const dateStr = dateFormat(date, 'Y-m-d');
		return !availableDatesSet.has(dateStr);
	}

	function generateApplianceChartData(data: PredictionsRes): ApplianceChartData {
		const labels: string[] = [];
		const keys: string[] = [];
		const currentValues: number[] = [];
		const colors: string[] = [];

		data.byAppliance.forEach((item) => {
			labels.push(capFirstChar(item.appliance));
			keys.push(item.appliance);
			currentValues.push(item.value);
			colors.push(applianceColorMap[item.appliance]);
		});

		return {
			labels,
			keys,
			currentValues,
			colors,
		};
	}

	function generateApplianceListData(data: PredictionsRes): ApplianceListData {
		let total = 0;
		data.byAppliance.forEach((item) => {
			total += item.value;
		});

		// Sort by value descending
		const sorted = [...data.byAppliance].sort((a, b) => b.value - a.value);

		return {
			appliances: sorted,
			total,
		};
	}

	function generatePhaseChartData(data: PredictionsRes): PhaseChartData {
		const currentValues = [data.byPhase.phaseA, data.byPhase.phaseB, data.byPhase.phaseC];
		const labels = ['Phase A', 'Phase B', 'Phase C'];

		return {
			currentValues,
			labels,
		};
	}

	export function getBarChartConfig(chartDataSet: {
		labels: string[];
		currentValues: number[];
	}): ChartDataInterface {
		return {
			name: 'dynamicChart',
			type: 'bar',
			units: 'W',
			labels: chartDataSet.labels,
			loaded: chartDataSet.labels.length > 0,
			series: [
				{
					name: 'current',
					visible: true,
					//@ts-ignore
					data: chartDataSet.currentValues,
					style: 'dynamicChart_current',
				},
			],
			height: DEFAULT_CHART_HEIGHT,
		};
	}

	$: pieChartData = predictionsData
		? getDynamicPieChartConfig(generateApplianceChartData(predictionsData))
		: null;
	$: phaseChartData = predictionsData
		? getBarChartConfig(generatePhaseChartData(predictionsData))
		: null;
</script>

<Toolbar class="ai-breakdown-toolbar mb-m">
	<Breadcrumbs
		aria-label="Energy Predictions"
		slot="breadcrumbs"
		links={[{ label: $format('text_energy_ai_breakdown', { default: 'AI Breakdown' }) }]}
	/>
	<svelte:fragment slot="rightSide">
		<div class="ai-breakdown-controls">
			<div class="ai-breakdown-range-controls">
				<Select
					aria-label="Select predictions mode"
					id="predictions-mode-select"
					icon="fas fa-clock-rotate-left"
					options={predictionsModeOptions}
					value={selectedModeOption}
					on:change={onModeChange}
				/>

				{#if predictionsMode === 'snapshot'}
					<!-- <Select
					aria-label="Current predictions time"
					id="predictions-now-select"
					icon="fas fa-calendar-alt"
					disabled
					options={[
						{
							value: 'now',
							title: 'Now',
						},
					]}
					value={{
						value: 'now',
						title: 'Now',
					}}
				/> -->
				{:else}
					<Datepicker
						aria-label="Select date for energy predictions"
						a11yProps={{
							inputAriaLabel: 'Select date for energy predictions',
							selectMonthAriaLabel: 'Select month',
							selectYearAriaLabel: 'Select year',
						}}
						id="predictions-date-picker"
						dateFormat={dpFormatD}
						mode="single"
						class="ml-m"
						minDate={addDate(today, 'y', -3)}
						{maxDate}
						{defaultMonthsOptions}
						options={{
							locale: {
								firstDayOfWeek,
								weekdays: {
									shorthand: weekdays,
									longhand: weekdays,
								},
							},
							disable: [isDateDisabled],
						}}
						value={[selectedDate]}
						on:change={onDateChange}
					/>
				{/if}
			</div>

			{#if predictionsMode === 'snapshot'}
				<div class="ai-breakdown-snapshot-status">
					<IconButton
						aria-label="Refresh snapshot"
						icon="fas fa-rotate"
						disabled={loading || (lastSnapshotTime !== null && minutesSinceSnapshot < 1)}
						{loading}
						on:click={() => fetchPredictions()}
					/>
					<Text element="span" class="ml-s text-color-muted">
						{#if minutesSinceSnapshot < 1}
							now
						{:else if minutesSinceSnapshot >= 60}
							{Math.floor(minutesSinceSnapshot / 60)}h
							{#if minutesSinceSnapshot % 60 > 0}
								{minutesSinceSnapshot % 60}m
							{/if}
						{:else}
							{minutesSinceSnapshot}m
						{/if}
					</Text>
				</div>
			{/if}
		</div>
	</svelte:fragment>
</Toolbar>

<CardGrid lg={500} sm={350} calcGridCols>
	<!-- Energy By Appliance Type - List View -->
	<ControlsContainer>
		<svelte:fragment slot="first">
			<Text class="text-bold text-capitalize mb-m" styleType="controls-large"
				>{$format('text_energy_by_appliance_type', {
					default: 'Energy By Appliance Type',
				})}</Text
			>
			{#if loading}
				<div class="d-flex justify-content-center align-items-center" style="height: 270px">
					<Loader size="m" />
				</div>
			{:else if error}
				<div in:fade|global={{ duration: 300 }}>
					<Text class="mb-m" styleType="body"
						>{$format('description_predictions_error', {
							default: 'Failed to load predictions. Please try again later.',
						})}</Text
					>
				</div>
			{:else if !predictionsData || predictionsData.byAppliance.length === 0}
				<div in:fade|global={{ duration: 300 }}>
					<Text class="mb-m" styleType="body"
						>{$format('description_predictions_no_data', {
							default: 'No prediction data available at the moment.',
						})}</Text
					>
				</div>
			{:else}
				<div in:fade|global={{ duration: 300 }}>
					<Scroll style="height:auto; max-height:270px">
						{@const listData = generateApplianceListData(predictionsData)}
						<Stack gap="s" class="mb-l">
							{#each listData.appliances as applianceObj}
								<ListItem
									title={capFirstChar(applianceObj.appliance)}
									icon="fa fa-microchip"
									viewType="bar"
									progress={{
										percent: (applianceObj.value / listData.total) * 80,
										title: convert(applianceObj.value, units),
										color: applianceColorSystemMap[applianceObj.appliance],
										showPercentBar: true,
									}}
								/>
							{/each}
						</Stack>
					</Scroll>
				</div>
			{/if}
		</svelte:fragment>
	</ControlsContainer>

	<!-- Energy By Appliance Type - Pie Chart -->
	<ControlsContainer>
		<svelte:fragment slot="first">
			<Text class="text-bold text-capitalize mb-m" styleType="controls-large"
				>{$format('text_energy_by_appliance_type', {
					default: 'Energy By Appliance Type',
				})}</Text
			>
			{#if loading}
				<div
					class="d-flex justify-content-center align-items-center"
					style={`height: ${chartHeight}px`}
				>
					<Loader size="m" />
				</div>
			{:else if error}
				<div in:fade|global={{ duration: 300 }}>
					<Text class="mb-m" styleType="body"
						>{$format('description_predictions_error', {
							default: 'Failed to load predictions.',
						})}</Text
					>
				</div>
			{:else if pieChartData && pieChartData.loaded}
				<LayerChartRenderer chartData={pieChartData} />
			{/if}
		</svelte:fragment>
	</ControlsContainer>

	{#if !loading && !error && predictionsData}
		<ControlsContainer color="color-2">
			<svelte:fragment slot="first">
				<Text class="text-bold"
					>{capFirstChar(
						$format('text_energy_distribution_by_phases', {
							default: 'Energy Distribution By Phases',
						}),
					)}</Text
				>
				{#if !phaseChartData || !phaseChartData.loaded}
					<div
						class="d-flex justify-content-center align-items-center"
						style={`height: ${phaseChartData?.height || 300}px`}
					>
						<Loader size="m" />
					</div>
				{:else}
					<LayerChartRenderer chartData={phaseChartData} />
				{/if}
			</svelte:fragment>
		</ControlsContainer>
	{/if}
</CardGrid>

<style>
	.ai-breakdown-controls,
	.ai-breakdown-range-controls,
	.ai-breakdown-snapshot-status {
		display: flex;
		align-items: center;
	}

	.ai-breakdown-controls,
	.ai-breakdown-range-controls {
		gap: var(--spacing-s);
	}

	@media screen and (max-width: 639px) {
		:global(.ai-breakdown-toolbar.toolbar) {
			grid-template-columns: 1fr;
			grid-template-areas:
				'breadcrumbs'
				'right-controls';
		}

		:global(.ai-breakdown-toolbar .right-controls-container),
		:global(.ai-breakdown-toolbar .right-controls-container > *) {
			justify-self: stretch;
			width: calc(100% + var(--spacing-s));
			margin: 0 0 0 calc(var(--spacing-s) * -1);
		}

		.ai-breakdown-controls,
		.ai-breakdown-range-controls {
			flex-direction: column;
			align-items: stretch;
			width: 100%;
		}

		.ai-breakdown-range-controls > :global(*) {
			width: 100%;
			margin-left: 0;
		}

		.ai-breakdown-snapshot-status {
			width: 100%;
		}
	}
</style>
